import axios, { AxiosError } from 'axios'
import { getDomainWithoutSubdomain } from '../utils/domainWithoutSubdomain'

// API Failover Configuration
const isLocalhost = window.location.hostname === 'localhost'
const baseDomain = getDomainWithoutSubdomain(window.location.host)

// สร้าง list ของ API endpoints สำหรับ failover
const envApiEndpoint = process.env.REACT_APP_API_ENDPOINT || ''
const API_ENDPOINTS = isLocalhost
	? [
		// envApiEndpoint,
		// envApiEndpoint.replace('api.', 'api2.'),
		envApiEndpoint.replace('api.', 'api3-j.'),
	]
	: [
		// `https://api.${baseDomain}/v2`,
		// `https://api2.${baseDomain}/v2`,
		`https://api3-j.${baseDomain}/v2`,
		// `https://api.fcn.bet/v2`,
		// `https://api2.fcn.bet/v2`,
		// `https://api3.fcn.bet/v2`,
		// `https://api4.${baseDomain}/v2`,
	]
// เก็บ index ของ API ที่กำลังใช้งานอยู่
let currentApiIndex = 0
let isInitialized = false
let initPromise: Promise<void> | null = null

// ดึง endpoint ปัจจุบัน
const getCurrentEndpoint = () => API_ENDPOINTS[currentApiIndex]

// ฟังก์ชันเช็ค health ของ API
const checkApiHealth = async (endpoint: string): Promise<boolean> => {
	try {
		// ลบ /v2 ออกจาก endpoint เพื่อเช็ค healthz ที่ base URL
		const healthUrl = endpoint.replace('/v2', '')
		// console.log(`[API Health] Checking: ${healthUrl}/`)
		const response = await axios.get(`${healthUrl}/`, {
			timeout: 5000,
			withCredentials: true
		})
		return response.status === 200
	} catch {
		return false
	}
}

// ฟังก์ชันหา API ที่ใช้งานได้ตั้งแต่เริ่มต้น (เช็คพร้อมกัน)
const findWorkingApi = async (): Promise<void> => {
	// console.log(`[API Init] Checking ${API_ENDPOINTS.length} endpoints in parallel...`)

	// สร้าง promise สำหรับแต่ละ endpoint
	const checkPromises = API_ENDPOINTS.map(async (endpoint, index) => {
		const isHealthy = await checkApiHealth(endpoint)
		if (isHealthy) {
			return { index, endpoint, success: true }
		}
		throw new Error(`Endpoint ${endpoint} is not healthy`)
	})

	try {
		// ใช้ตัวแรกที่ตอบกลับสำเร็จ
		const result = await Promise.any(checkPromises)
		currentApiIndex = result.index
		baseRequest.defaults.baseURL = result.endpoint
		// console.log(`[API Init] Using: ${result.endpoint}`)
	} catch {
		// ถ้าทุกตัวล้มเหลว ใช้ตัวแรกเป็น fallback
		console.error('[API Init] All API endpoints are unavailable, using first one as fallback')
	}
}

// ฟังก์ชัน initialize API - เรียกครั้งเดียวตอนเริ่มต้น
const initializeApi = async (): Promise<void> => {
	if (isInitialized) return
	if (initPromise) return initPromise

	initPromise = findWorkingApi().then(() => {
		isInitialized = true
	})

	return initPromise
}

// ฟังก์ชันสลับไปใช้ API ถัดไปที่ใช้งานได้
const switchToNextWorkingApi = async (): Promise<boolean> => {
	const startIndex = currentApiIndex

	for (let i = 1; i <= API_ENDPOINTS.length; i++) {
		const nextIndex = (startIndex + i) % API_ENDPOINTS.length
		const endpoint = API_ENDPOINTS[nextIndex]
		// console.log(`[API Failover] Checking: ${endpoint}`)

		if (await checkApiHealth(endpoint)) {
			currentApiIndex = nextIndex
			baseRequest.defaults.baseURL = endpoint
			// console.log(`[API Failover] Switched to: ${endpoint}`)
			return true
		}
	}

	console.error('[API Failover] All API endpoints are unavailable')
	return false
}

const baseRequest = axios.create({
	baseURL: getCurrentEndpoint(),
	withCredentials: true,
})

baseRequest.interceptors.request.use(async (config) => {
	// เช็ค health ก่อน request แรก
	await initializeApi()

	// ใช้ endpoint ที่เช็คแล้วว่าใช้งานได้
	config.baseURL = getCurrentEndpoint()

	const token = localStorage.getItem('x-auth')
	if (window.location.hostname === 'localhost') {
		const prefix = "WK89"

		return {
			...config,
			headers: {
				...config.headers,
				...(prefix && { 'X-Prefix': prefix }),
				...(token && { Authorization: `Bearer ${token}` }),
			},
		}
	}
	return {
		...config,
		headers: {
			...config.headers,
			...(token && { Authorization: `Bearer ${token}` }),
		},
	}
})

baseRequest.interceptors.response.use(
	(res) => res.data,
	async (err: AxiosError) => {
		const originalRequest = err.config

		// ถ้าเจอ status 500 หรือ network error ให้ลอง failover
		if (
			!isLocalhost &&
			originalRequest &&
			(err.response?.status === 500 || !err.response)
		) {
			// console.log('[API Failover] Detected error, attempting to switch API...')

			const switched = await switchToNextWorkingApi()
			if (switched) {
				// Retry request ด้วย endpoint ใหม่
				originalRequest.baseURL = getCurrentEndpoint()
				return baseRequest(originalRequest)
			}
		}

		if (err.response) {
			return Promise.reject(err.response.data)
		}

		// if (API_ENDPOINT?.indexOf('v2.api.com') !== -1) {
		// 	const investigateKey = 'nextInvestigate'
		// 	const nextInvestigateTimestamp = window.localStorage.getItem(investigateKey)
		// 	if (nextInvestigateTimestamp && parseInt(nextInvestigateTimestamp) > Date.now()) {
		// 		return Promise.reject(err.message)
		// 	}

		// 	const delayMS = 15 * 60 * 1000
		// 	window.localStorage.setItem(investigateKey, (Date.now() + delayMS).toString())

		// 	const rdr = encodeURIComponent(window.location.href)
		// 	window.location.href = `https://v2.api.com/investigate?rdr=${rdr}`
		// 	return Promise.reject({ message: 'กำลังพาคุณไปยังหน้ายืนยันตัวตน' })
		// }
		return Promise.reject({
			message: 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ในขณะนี้',
		})
	}
)

export default baseRequest
